import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, X, Film, ImagePlus, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO_CATEGORIES = ['Tutorial', 'Review', 'Routine', 'Unboxing/Haul', 'Other'];
const MAX_DURATION_SEC = 60;
const MAX_FILE_SIZE_MB = 50;

export default function CreateVideoPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const [form, setForm] = useState({ title: '', description: '', category: '' });
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Check if user is verified influencer
  const isVerifiedInfluencer = user?.profile?.is_influencer && user?.profile?.influencer_verification_status === 'approved';

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`Video must be under ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    // Check duration using HTML5 video element
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    videoEl.onloadedmetadata = () => {
      URL.revokeObjectURL(videoEl.src);
      if (videoEl.duration > MAX_DURATION_SEC) {
        toast.error(`Video must be ${MAX_DURATION_SEC} seconds or shorter`);
        return;
      }
      setVideoDuration(Math.round(videoEl.duration));
      setVideoFile(file);
      setVideoPreviewUrl(URL.createObjectURL(file));
    };
    videoEl.onerror = () => {
      toast.error('Could not read video file');
    };
    videoEl.src = URL.createObjectURL(file);
  };

  const handleThumbnailSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Thumbnail must be under 5MB');
      return;
    }
    setThumbnailFile(file);
    setThumbnailPreviewUrl(URL.createObjectURL(file));
  };

  const clearVideo = () => {
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoDuration(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const clearThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreviewUrl(null);
    if (thumbInputRef.current) thumbInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Please add a title'); return; }
    if (!form.category) { toast.error('Please select a category'); return; }
    if (!videoFile) { toast.error('Please upload a video'); return; }

    setSubmitting(true);
    setUploadProgress(0);
    try {
      // Upload video file
      setUploading(true);
      const videoFormData = new FormData();
      videoFormData.append('file', videoFile);
      const videoRes = await axios.post(`${API}/upload`, videoFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 80));
        }
      });
      const video_url = `${process.env.REACT_APP_BACKEND_URL}${videoRes.data.url}`;

      // Upload thumbnail if provided
      let thumbnail_url = null;
      if (thumbnailFile) {
        setUploadProgress(85);
        const thumbFormData = new FormData();
        thumbFormData.append('file', thumbnailFile);
        const thumbRes = await axios.post(`${API}/upload`, thumbFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        thumbnail_url = `${process.env.REACT_APP_BACKEND_URL}${thumbRes.data.url}`;
      }

      setUploadProgress(90);
      setUploading(false);

      // Create video post
      await axios.post(`${API}/videos`, {
        title: form.title.trim(),
        description: form.description.trim() || null,
        category: form.category,
        video_url,
        thumbnail_url,
      });

      setUploadProgress(100);
      toast.success('Video posted!');
      navigate(`/profile/${user.user_id}`, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Failed to post video');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  if (!isVerifiedInfluencer) {
    return (
      <div className="min-h-screen bg-[#FCFAF8]">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Film size={48} className="mx-auto text-cream-200 mb-4" />
          <h2 className="font-heading text-2xl text-[#2C2A29] mb-2">Influencer Content Only</h2>
          <p className="font-body text-[#78716C] mb-6">Only verified influencers can post video content.</p>
          <button onClick={() => navigate('/')} className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3 font-body font-semibold hover:bg-[#ECAEAE] transition-all" data-testid="go-home-btn">
            Back to Home
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-body text-sm text-[#78716C] hover:text-[#2C2A29] mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-cream-100/50">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#FFF5F5] flex items-center justify-center">
              <Film size={20} className="text-[#D48585]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl text-[#2C2A29]">Post Content</h1>
              <p className="font-body text-xs text-[#A89F91]">Share tutorials, tips & tricks with your followers</p>
            </div>
          </div>

          {/* BETA limits notice */}
          <div className="bg-[#FFF5F5] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="beta-limits-notice">
            <AlertCircle size={14} className="text-[#D48585] mt-0.5 flex-shrink-0" />
            <p className="text-xs font-body text-[#78716C]">
              <span className="font-semibold text-[#D48585]">BETA limits:</span> Up to 20 videos total. Each video can be up to {MAX_DURATION_SEC} seconds and {MAX_FILE_SIZE_MB} MB
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Video Upload */}
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Video *</label>
              {videoPreviewUrl ? (
                <div className="relative rounded-2xl overflow-hidden bg-black">
                  <video src={videoPreviewUrl} controls className="w-full max-h-[300px] object-contain" data-testid="video-preview" />
                  <button type="button" onClick={clearVideo}
                    className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70 transition-colors"
                    data-testid="remove-video-btn">
                    <X size={14} />
                  </button>
                  {videoDuration && (
                    <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs font-body px-2 py-0.5 rounded-full">
                      {videoDuration}s / {MAX_DURATION_SEC}s
                    </span>
                  )}
                </div>
              ) : (
                <button type="button" onClick={() => videoInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-cream-200 rounded-2xl p-8 text-center hover:border-[#F4C2C2] hover:bg-[#FFF5F5]/30 transition-all group"
                  data-testid="video-upload-area">
                  <Upload size={28} className="mx-auto text-cream-300 group-hover:text-[#D48585] transition-colors mb-2" />
                  <p className="font-body text-sm font-medium text-[#78716C]">Click to upload video</p>
                  <p className="font-body text-xs text-[#A89F91] mt-1">MP4, MOV, WebM up to {MAX_FILE_SIZE_MB}MB, max {MAX_DURATION_SEC}s</p>
                </button>
              )}
              <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={handleVideoSelect} data-testid="video-file-input" />
            </div>

            {/* Thumbnail Upload */}
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Thumbnail (optional)</label>
              {thumbnailPreviewUrl ? (
                <div className="relative w-40 aspect-[9/12] rounded-xl overflow-hidden">
                  <img src={thumbnailPreviewUrl} alt="Thumbnail" className="w-full h-full object-cover" data-testid="thumbnail-preview" />
                  <button type="button" onClick={clearThumbnail}
                    className="absolute top-1.5 right-1.5 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 transition-colors"
                    data-testid="remove-thumbnail-btn">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => thumbInputRef.current?.click()}
                  className="w-40 aspect-[9/12] border-2 border-dashed border-cream-200 rounded-xl flex flex-col items-center justify-center hover:border-[#F4C2C2] hover:bg-[#FFF5F5]/30 transition-all group"
                  data-testid="thumbnail-upload-area">
                  <ImagePlus size={20} className="text-cream-300 group-hover:text-[#D48585] transition-colors mb-1" />
                  <p className="font-body text-[10px] text-[#A89F91]">Add cover</p>
                </button>
              )}
              <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={handleThumbnailSelect} data-testid="thumbnail-file-input" />
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Title *</label>
              <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., My Morning Skincare Routine"
                maxLength={100}
                className="w-full bg-[#FCFAF8] border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent transition-all"
                data-testid="video-title-input" />
              <p className="text-xs font-body text-[#A89F91] mt-1 text-right">{form.title.length}/100</p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Category *</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="w-full rounded-xl h-12 bg-[#FCFAF8] border-cream-100 font-body" data-testid="video-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {VIDEO_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-body font-medium text-[#78716C] mb-2">Description (optional)</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Tell viewers what this video is about..."
                maxLength={500}
                rows={3}
                className="w-full bg-[#FCFAF8] border border-cream-100 rounded-xl px-4 py-3 font-body text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent transition-all resize-none"
                data-testid="video-description-input" />
              <p className="text-xs font-body text-[#A89F91] mt-1 text-right">{form.description.length}/500</p>
            </div>

            {/* Upload Progress */}
            {submitting && (
              <div className="space-y-2" data-testid="upload-progress">
                <div className="flex items-center justify-between text-xs font-body">
                  <span className="text-[#78716C]">{uploading ? 'Uploading video...' : 'Creating post...'}</span>
                  <span className="text-[#C5A880] font-semibold">{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-cream-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#F4C2C2] to-[#C5A880] rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}

            {/* Submit */}
            <button type="submit" disabled={submitting || !videoFile || !form.title.trim() || !form.category}
              className="w-full bg-[#2C2A29] text-white rounded-full px-6 py-3.5 font-body font-semibold transition-all hover:bg-[#1a1918] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="post-video-submit">
              <Film size={16} />
              {submitting ? 'Posting...' : 'Post Video'}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
